/* global isLocal */
import striptags from "striptags";
/**
 * BatchGeoTable is a JavaScript library built for BatchGeo to facilitate the
 * display, sorting, filtering, and interactions of large BatchGeo datasets.
 * The library by itself creates a table from a BatchGeo dataset and displays it
 * in a given container and does some fancy rendering so that scrolling the data
 * isn't laggy.
 *
 * For sorting, filtering, and other stuff BatchGeo allows for easy plugin
 * writing. More info on how to add plugins on the constructor and plugin docs.
 *
 * TIP: The goal of this library is just for the rendering of tabular data and
 * provides APIs to manipulate that display, but the library itself shouldn't
 * be add features outside that scope. Things outside that scope are plugins.
 */
(function () {
  /**
   * This is the BatchGeoTable constructor. It takes an options hash with some
   * required options:
   *
   * - container: The element to inject the table into. This is NOT a selector
   * - data: The data for the table. This expects a BatchGeo object setup
   *
   * There's also optional options:
   * - plugins: An array of plugin function definitions. Plugins are just
   *   functions that get the BatchGeoTable instance passed to it.
   *
   * Example initialization:
   *
   * var batchGeoTable = new BatchGeoTable({
   *   data: data,
   *   container: $('.foo')[0],
   *   plugins: [
   *     HeaderSort
   *   ]
   * });
   *
   * BatchGeoTable also comes with an event emitter meaning you, and plugins,
   * can hook into events and also listen to them. For example, if you wanted
   * to do something every time the row sort changed in your code you could do
   *
   * batchGeoTable.addListener('rowupdate', function () {
   *  // Rows were updated!
   * });
   *
   * addListener, removeListener, and emit all have docs below above their
   * function definitions.
   */
  function BatchGeoTable(options) {
    this.options = options;
    this.container = options.container;
    this._handlers = {};
    this.importData(options.data);
    this._init();
    BatchGeoStore.dispatch({ type: "TABLE_LOADED" });
    return this;
  }

  /**
   * Use this method to import data into BatchGeoTable. It doesn't update or
   * render anything automatically. It let's you decide how to best update
   * BatchGeoTable after the data is imported and formatted. It saves data onto
   * the prototype as the `data` key.
   *
   * importData should try to only save as much data as it *needs* and no more.
   * Since there's is mostly duplicate data between dataRS and mapRS we only
   * auto-include everything from dataRS. If there's something in mapRS or some
   * other property on the data you're importing this is where you would like to
   * add to the internal data, manually merge it in here like the distance props.
   */
  BatchGeoTable.prototype.importData = function (data) {
    // First clone our data as to not mess up our data for others
    var clonedData = $.extend(true, {}, data);
    var formattedData = {
      columns: [],
      rows: [],
    };

    clonedData.columnNames.forEach(function (column) {
      formattedData.columns.push(column);
    });
    clonedData.dataRS.forEach(function (row, i) {
      // Trim the row data down to only be as many columns as the header. If we
      // don't do this a user can have less header columns then data columns
      // which causes nasty layout issues
      row.length = clonedData.columnNames.length;
      const rM = window.per.mapRS[i];
      formattedData.rows.push({
        id: i,
        data: row,
        valid: !rM.invalid && BatchGeo.goodLatLon(rM.lt, rM.ln),
      });
    }, this);

    this.data = formattedData;
    this._displayData = $.extend(
      true,
      [],
      this.data.rows.filter((r) => r.valid),
    );
    return this;
  };

  /**
   * Convert the displayData into a format that we can easily link up as we loop
   * through this.data rows. We make it into an object like {5: row} where
   * 5 == this.data index.
   *
   * NOTE: This gives you back the pointers and does NOT clone. This is why it's
   * a private method. Use it to update display data.
   *
   * @return {object} The display data as an object.
   * @private
   */
  BatchGeoTable.prototype._getDisplayDataAsObject = function () {
    var displayDataCollection = {};
    this._displayData.forEach(function (row) {
      displayDataCollection[row.id] = row;
    });
    return displayDataCollection;
  };

  /**
   * Adds a new column with data to BGT. It allows you to also select the exact
   * index you want the column inserted.
   *
   * @param {string} newColumnName Name of your column to be used in the thead
   * @param {array} newRows An array of data for your new column
   * @param {number} [insertIndex] The index to insert the column. Example: 0
   *                               would be first, 1 is 2nd, and so on
   *
   * @return {undefined}
   */
  BatchGeoTable.prototype.addColumn = function (
    newColumnName,
    newRows,
    insertIndex,
  ) {
    var displayDataCollection = this._getDisplayDataAsObject();
    insertIndex = insertIndex || 0;
    // Add the new column
    this.data.columns.splice(insertIndex, 0, newColumnName);

    // Add the new data for every row in the app
    this.data.rows.forEach(function (row, rowIndex) {
      row.data.splice(insertIndex, 0, newRows[rowIndex]);

      var displayRow = displayDataCollection[rowIndex];

      if (displayRow) {
        displayRow.data.splice(insertIndex, 0, newRows[rowIndex]);
      }
    }, this);

    this.emit("addedcolumn", insertIndex);
  };

  /**
   * Updates an existing column with new data to BGT. It allows you to also
   * select the exact index you want the column inserted.
   *
   * @param {array} newRows An array of data for your new column
   * @param {number} [insertIndex] The index to insert the column. Example: 0
   *                               would be first, 1 is 2nd, and so on
   *
   * @return {undefined}
   */
  BatchGeoTable.prototype.updateColumn = function (newRows, insertIndex) {
    if (insertIndex === this.data.columns.indexOf("Distance")) {
      this._renderHeaders();
    }

    var displayDataCollection = this._getDisplayDataAsObject();
    insertIndex = insertIndex || 0;
    // Add the new data for every row in the app
    this.data.rows.forEach(function (row, rowIndex) {
      row.data[insertIndex] = newRows[rowIndex];

      var displayRow = displayDataCollection[rowIndex];

      if (displayRow) {
        displayRow.data[insertIndex] = newRows[rowIndex];
      }
    }, this);

    this.emit("updatedcolumn", insertIndex);
  };

  /**
   * Remove a column at the specified index.
   *
   * @param {number} removeIndex Index of the column to remove where 0 is first
   *                             1 is 2nd, and so on. -1 is last.
   *
   * @return {undefined}
   */
  BatchGeoTable.prototype.removeColumn = function (removeIndex) {
    removeIndex = removeIndex || 0;

    // Remove the new column
    this.data.columns.splice(removeIndex, 1);

    // Remove the new data for every row in the app
    this.data.rows.forEach(function (row) {
      row.data.splice(removeIndex, 1);
    });

    this._displayData.forEach(function (displayRow) {
      displayRow.data.splice(removeIndex, 1);
    });
  };

  /**
   * Renders the HTML for the table inside the given container element. It also
   * initializes the scroller functionality that handles large datasets. This
   * is automatically called during initialization but you can recall it if you
   * need to rerender the whole table again with the same data.
   *
   * You'll notice there's a "faux" table and thead in the DOM template below.
   * This is so we can have a sticky header. The faux table is *outside* the
   * scroll area so it will appear fixed. We need both theads so that the widths
   * will match because the content matches since HTML will resize the table
   * based on the widths of the table data. Later with JS we'll push the thead
   * in the scroll area up and behind the faux one to make the faux one look
   * like its part of the table.
   *
   * NOTE: This does NOT render the row data. That should be handled by the
   * scroller. If you're updating the data make sure you do that first before
   * you call render.
   *
   * @returns {undefined}
   */
  BatchGeoTable.prototype._init = function () {
    this.container.innerHTML =
      '<div class="clusterize batchgeo-table">' +
      '<div class="clusterize-headers">' +
      '<table class="clusterize-faux-table">' +
      '<thead class="clusterize-faux-thead">' +
      "<tr></tr>" +
      "</thead>" +
      "</table>" +
      "</div>" +
      '<div class="clusterize-scroll">' +
      '<table class="clusterize-table">' +
      '<thead class="clusterize-thead">' +
      "<tr></tr>" +
      "</thead>" +
      '<tbody class="clusterize-content clusterize-no-data"></tbody>' +
      "</table>" +
      "</div>" +
      "</div>";

    this._cacheElements();
    this._loadPlugins();
    this._initializeScroller();
  };

  /**
   * Renders the HTML for the table headers and builds all rows in the _displayData
   *
   * @return {object} the BatchGeoTable instance
   */
  BatchGeoTable.prototype.render = function () {
    this._renderHeaders();
    this._buildRows();
    return this;
  };

  /**
   * A private method that builds HTML for the table head and updates column widths
   *
   * @returns {undefined}
   */
  BatchGeoTable.prototype._renderHeaders = function () {
    var columnsHTML = this._buildColumns();
    var els = this.getElements();

    els.headerContainer.innerHTML = "<tr>" + columnsHTML + "</tr>";
    els.fauxHeaderContainer.innerHTML = "<tr>" + columnsHTML + "</tr>";

    var $headerContainerClone = $(els.headerContainer)
      .clone()
      .appendTo(els.contentContainer);

    // Hide the real header container behind the faux header container by making
    // the table go up the height of the real header
    $(els.table).css("margin-top", -$headerContainerClone.outerHeight());

    // Now that we're done measuring remove it
    $headerContainerClone.remove();

    this._updateFauxHeaderWidths();
    this.emit("builtheaders");
  };

  /**
   * Hides the table
   *
   * @returns {undefined}
   */
  BatchGeoTable.prototype.hide = function hide() {
    $(this.container).hide();
  };

  /**
   * Shows the table
   *
   * @returns {undefined}
   */
  BatchGeoTable.prototype.show = function show() {
    $(this.container).show();
  };

  /**
   * Resizes BatchGeoTable to fit inside the container. Call when the container
   * is resized.
   */
  BatchGeoTable.prototype.resize = function () {
    this._updateFauxHeaderWidths();
  };

  /**
   * Caches frequently used elements and makes it so if the DOM changes you
   * only need to change the selector here and not across the whole library.
   * PLEASE use these selectors when you can and if you can't add them here so
   * selectors can be updated in one place.
   *
   * @return {object} The cached elements (just like what getElements gives you)
   */
  BatchGeoTable.prototype._cacheElements = function () {
    var query = function (selector) {
      return this.container.querySelector(selector);
    }.bind(this);

    return (this._elementCache = {
      container: this.container,
      scrollArea: query(".clusterize-scroll"),
      contentContainer: query(".clusterize-content"),
      table: query(".clusterize-table"),
      headerContainer: query(".clusterize-thead"),
      fauxTable: query(".clusterize-faux-table"),
      fauxHeaderContainer: query(".clusterize-faux-thead"),
    });
  };

  /**
   * Returns the private cached element object which is internally updated as
   * needed. See _cacheElements for a list of key->values for selectors.
   *
   * @return {object} The publicly available cached elements
   */
  BatchGeoTable.prototype.getElements = function () {
    return this._elementCache;
  };

  /**
   * A private method that builds the column HTML for the table head
   */
  BatchGeoTable.prototype._buildColumns = function () {
    var columnHtml = "";
    this.data.columns.forEach(function (name, i) {
      var text = name.replace(/ /g, "&nbsp;");
      columnHtml += `<th data-position="${i}">${text === "Distance" && BatchGeoStore.getState().MarkerMeasure.enabled ? `${text} (${BatchGeoStore.getState().App.units})` : text}</th>`;
    });

    return columnHtml;
  };

  /**
   * Updates the faux header widths to match the real header widths. It's
   * important that in your CSS you have border-box set. This will ensure that
   * the width passed here is the width that will be displayed no matter what
   * padding or borders you have applied.
   *
   * @return {object} the BatchGeoTable instance
   */
  BatchGeoTable.prototype._updateFauxHeaderWidths = function () {
    var els = this.getElements();

    if (els.table.getBoundingClientRect().width > window.innerWidth) {
      els.table.classList.add("data-table-width-restrict");
      els.fauxTable.classList.add("data-table-width-restrict");
    }

    $(els.fauxTable).css("width", $(els.table).innerWidth());

    $(els.contentContainer)
      .find("[data-id]")
      .first()
      .find("td")
      .each(function (i) {
        $(els.fauxHeaderContainer)
          .find("th")
          .eq(i)
          .outerWidth($(this).outerWidth());
      });

    this._redrawFauxHeader();

    return this;
  };

  /**
   * Some browsers *cough* Chrome & Safari *cough* don't redraw these table
   * headers for some reason. This code forces the browser to redraw the
   * elements because it hides it completely and then shows it. Here's a SO
   * thread where I described the problem in more detail:
   * http://stackoverflow.com/questions/34145306/during-resize-chrome-and-safari-dont-update-styles-reliably-webkit-bug
   *
   * @return {object} the BatchGeoTable instance
   */
  BatchGeoTable.prototype._redrawFauxHeader = function () {
    $(this.getElements().fauxHeaderContainer).hide().show(0);

    return this;
  };

  /**
   * In the render() method docs we explain a bit why we need two theads and
   * thats for the sticky header. However, since it's not in the same scroll
   * area, when we scroll left/right the faux table head doesn't scroll with it
   * because it's a totally different element. To achieve the effect that you
   * are scrolling the same table we need to "sync" the scroll position of the
   * two tables. We do this here by setting the margin of the faux table
   * negative to the scrollLeft of the scrolling area.
   *
   * @return {object} the BatchGeoTable instance
   */
  BatchGeoTable.prototype._initHeaderPositionSync = function () {
    var self = this;
    var els = this.getElements();

    var lastScrollLeft = 0;

    $(els.scrollArea).on("scroll", function () {
      var newScrollLeft = els.scrollArea.scrollLeft;
      // header position sync only cares about left/right scrolling so to not
      // trigger 1000s of unnecessary scroll events we check if the new scroll
      // left is NOT the same as it was last time. If you scroll up/down it will
      // be the same. If you scroll left/right it'll be different.
      if (newScrollLeft !== lastScrollLeft) {
        els.fauxTable.style.marginLeft = "-" + newScrollLeft + "px";
        self._redrawFauxHeader();
        lastScrollLeft = newScrollLeft;
      }
    });

    return this;
  };

  /**
   * Converts a string such as "hello" to "█████". It sets the length of the
   * ascii blocks to be a range of string length +/- the given variance. So if
   * you pass a string with 10 characters, and give it a variance of 3 the
   * output can be 7-13 █s.
   *
   * @param  {string} str        The string to convert
   * @param  {number} [variance] How much variance to the you want (default: 3)
   * @return {string}            A string of █ characters
   */
  BatchGeoTable.prototype._convertTextToAsciiBlock = function (
    str,
    variance = 3,
  ) {
    var strLength = str.length;
    var max = strLength + variance;
    var min = strLength - variance;

    // If the min amount after subtraction is less than the given variance
    // amount then set it to the variance as the min to prevent negatives or 0s.
    if (min < variance) min = variance;

    // Returns a random number between the max and min set above
    var randomBlockLength = Math.round(Math.random() * (max - min) + min);

    return Array(randomBlockLength).join("█");
  };

  /**
   * Generates the given amount of rows meant to be placeholders while rows are
   * being rendered.
   *
   * @param  {number} [amount] Number of rows. (default: 50)
   * @return {Array}           Array of HTML strings for Clusterize to render
   */
  BatchGeoTable.prototype._generateLoadingRows = function (amount) {
    var self = this;
    var i = 0;
    var rowHtml = [];
    amount = amount || 50;
    while (i < amount) {
      var tdHtml = "";
      self.data.columns.forEach(function (header, headerIndex) {
        var tdWidth = $(self.getElements().fauxHeaderContainer)
          .find("th")
          .eq(headerIndex)
          .outerWidth();
        tdHtml +=
          '<td><div class="clusterize-no-data" style="width: ' +
          tdWidth +
          'px">' +
          self._convertTextToAsciiBlock(header) +
          "</div></td>";
      });
      rowHtml.push("<tr>" + tdHtml + "</tr>");
      i++;
    }
    return rowHtml;
  };

  /**
   * Creates a row worker that generates the row HTML.
   * See build-rows-worker.js for more docs
   *
   * @return {object} A web worker instance
   */
  BatchGeoTable.prototype._createRowWorker = function _createRowWorker() {
    return new XssSafeWorker(BatchGeoStore.getState().App.buildRowsWorkerUrl);
  };

  /**
   * Prevents the the user from swiping to go back or forward in their browser
   * history when horizontally scrolling the table. We do this by preventing
   * negative scrollTop/Left by forcing it to always be 0 or the max Top or Left
   * scroll position.
   *
   * @return {undefined}
   */
  BatchGeoTable.prototype._preventBrowserHistorySwipes = function () {
    this.getElements().scrollArea.addEventListener(
      "mousewheel",
      function (event) {
        // We don't want to scroll below zero or above the width and height
        var maxX = this.scrollWidth - this.offsetWidth;
        var maxY = this.scrollHeight - this.offsetHeight;

        // If this event looks like it will scroll beyond the bounds of the
        // element, prevent it and set the scroll to the boundary manually
        if (
          this.scrollLeft + event.deltaX < 0 ||
          this.scrollLeft + event.deltaX > maxX ||
          this.scrollTop + event.deltaY < 0 ||
          this.scrollTop + event.deltaY > maxY
        ) {
          event.preventDefault();

          // Manually set the scroll to the boundary
          this.scrollLeft = Math.max(
            0,
            Math.min(maxX, this.scrollLeft + event.deltaX),
          );
          this.scrollTop = Math.max(
            0,
            Math.min(maxY, this.scrollTop + event.deltaY),
          );
        }
      },
      false,
    );
  };

  /**
   * The private _initializeScroller method currently uses a plugin called
   * Clusterize that makes scrolling giant datasets silky smooth. Here we
   * initialize it and build the rows for it. The data given to Clusterize is
   * an array of HTML strings for the rows.
   */
  BatchGeoTable.prototype._initializeScroller = function () {
    var self = this;
    var els = this.getElements();

    // This will be the last row selected while the container is hidden,
    // Allows us to properly set the scroll when the container is reopened

    var changeCount = 0;
    self._clusterize = new Clusterize({
      rows: self._generateLoadingRows(500),
      scrollElem: els.scrollArea,
      contentElem: els.contentContainer,
      callbacks: {
        clusterChanged: function () {
          // Remove the no-data class on the second clusterChange as the first will
          // be placeholder data
          if (!changeCount < 2) changeCount += 1;
          if (changeCount === 2) {
            $(els.contentContainer).removeClass("clusterize-no-data");
          }

          self._updateFauxHeaderWidths();
          self.emit("rowupdate");
        },
      },
    });

    // Update headers width on window resize since the table will change size
    // thus changing the widths of the table cells
    $(window).resize(function () {
      self._updateFauxHeaderWidths();
    });

    self._initHeaderPositionSync();
    self._preventBrowserHistorySwipes();

    return this;
  };

  /**
   * removeHTML is responsible for removing nested HTML from displayData strings.
   * @param {*} displayData
   * @returns {*} copy of displayData with HTML tags removed from strings
   */
  BatchGeoTable.prototype.removeHTML = function (displayData) {
    const mutableDisplayData = $.extend(true, [], displayData);
    mutableDisplayData.forEach((row) => {
      // first pass removes all html except those specified in second arguement of striptags
      row.data = row.data.map((dataString) => {
        if (typeof dataString !== "string") return dataString;
        return striptags(dataString, ["br"]);
      });
      // second pass replaces remaining html with spaces
      row.data = row.data.map((dataString) => {
        if (typeof dataString !== "string") return dataString;
        return striptags(dataString, [], " ");
      });
    });
    return mutableDisplayData;
  };

  /**
   * _buildRows is in charge or generating the markup for the current displayData
   * It uses a worker thread as to not block the browser on large datasets
   *
   * @param {boolean} [refresh=true] Whether to trigger a clusterize refresh
   * @param {function} [callback] A function to call once the update is done
   * @return {undefined}
   */
  BatchGeoTable.prototype._buildRows = function (refresh, callback) {
    const self = this;
    const rowWorker = this._createRowWorker();
    refresh = typeof refresh === "undefined" ? true : refresh;
    const cleanDisplayData = this.removeHTML(this._displayData);

    // search marker generates with a changed function
    // and breaks if it is removed, however if we pass a function
    // postMessage() breaks, so we clear the search marker first.
    if (window.per.searchMarker) {
      window.per.searchMarker.setMap(null);
      window.per.searchMarker = null;
    }

    const context = { ...window.per };
    if (BatchGeoStore.getState().MarkerMeasure.enabled) {
      context.markerMeasure = true;
    }

    // This is anti-pattern as the header sorting is provided via plugin and should
    // not be relied upon in the base table object, for now we'll leave it here
    // as there is more involved in pulling it out and allowing for a callback as we're
    // using workers, the underlying idea is that whenever we add or remove rows we should
    // take into account the current order that has been applied
    if (BatchGeoStore.getState().HeaderSort) {
      rowWorker.postMessage({
        rows: cleanDisplayData,
        order: BatchGeoStore.getState().HeaderSort.order,
        context,
      });
    } else {
      rowWorker.postMessage({
        rows: cleanDisplayData,
        context,
      });
    }

    const onMessage = (message) => {
      if (message.data.rows) {
        self._displayData = message.data.rows;
        self._clusterize.update(message.data.tableData);

        if (refresh) {
          self._clusterize.refresh();
        }

        self.emit("rowsbuilt");
        if (callback) callback();
      }
    };

    /**
     * worker.onerror recieves an error event from the worker instance
     * https://developer.mozilla.org/en-US/docs/Web/API/Worker/error_event
     * https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent
     * @param {Event} event
     */
    const onError = (event) => {
      console.error("Unexpected error in rows-worker. Failed to build rows.");
      if (!event.message) {
        const { error, lineno, colno, filename } = event;
        console.error(`${filename} - ${lineno}:${colno}`);
        console.error(error);
        throw event.error;
      } else {
        throw event.message;
      }
    };

    /**
     * onmessageerror recieves an error event if a message fails to send or be deserialized
     * https://developer.mozilla.org/en-US/docs/Web/API/Worker/messageerror_event
     * @param {Event} event
     */
    const onMessageError = (event) => {
      const message = `Error receiving message from worker: ${event.message || JSON.stringify(event.error)}`;
      console.error(message);
      throw message;
    };

    const receiver = isLocal ? window : rowWorker;
    receiver.onmessage = onMessage;
    receiver.onerror = onError;
    receiver.onmessageerror = onMessageError;
  };

  /**
   * Use updateRowDisplay to alter the data for sorting or filtering. In other
   * words, use this when you want to update the display of the rows, but not
   * the actual data itself. The new table HTML is built in a worker to prevent
   * the browser from locking up as the display is updated.
   *
   * NOTE: The `rows` parameter expects the a BatchGeoTable data object. It
   * expects you to take data from this.data and sort/filter that. That format,
   * if done manually, looks like this:
   *
   * [{
   *   "id": 0, // index of data in dataRS
   *   "data": [ "X0", "Y0",  "Z0" ], // dataRS data
   * }]
   *
   * @param {array} rows The new rows you want to display
   * @param {function} [callback] A function to call once the update is done
   * @return {undefined}
   */
  BatchGeoTable.prototype.updateRowDisplay = function (rows, callback) {
    this.setDisplayData(rows);
    this._buildRows(false, callback);
  };

  /**
   * Resets the row display back to the original row display.
   *
   * @param {function} [callback] A function to call once the update is done
   * @return {undefined}
   */
  BatchGeoTable.prototype.resetRowDisplay = function (callback) {
    this.updateRowDisplay(this.data.rows, callback);
  };

  /**
   * getDisplayData is used for dealing with the data as displayed to the user.
   * For example in scrollToRow we use this to find where in the table a row
   * actually is because it might be sorted or filtered out from a plugin
   *
   * @return {object} row data from this.data but in the displayed order
   */
  BatchGeoTable.prototype.getDisplayData = function () {
    BatchGeo.timer("Start getDisplayData");
    var displayData = Object.assign([], this._displayData);
    BatchGeo.timer("End getDisplayData");
    return displayData;
  };

  /**
   * setDisplayData is used for setting the data as displayed to the user.
   * For example, when filtering we want to change the displayed values before
   * we trigger an update as we may change these values again before render
   *
   * @param {array} rows an array of row objects
   * @return {undefined}
   */
  BatchGeoTable.prototype.setDisplayData = function (rows) {
    this._displayData = rows;
  };

  /**
   * resetDisplayData is used for resetting the data as displayed to the user
   * back to the default state without triggering a re-render
   *
   * @return {undefined}
   */
  BatchGeoTable.prototype.resetDisplayData = function () {
    this._displayData = this.data.rows;
  };

  /**
   * Trigger table rows to be rebuilt, useful after manual calls to `setDisplayData`.
   *
   * @return {undefined}
   */
  BatchGeoTable.prototype.rebuildRows = function () {
    this._buildRows();
  };

  /**
   * Given an ID or array of IDs it returns *cloned* data for those IDs. It will
   * ignore IDs that do not exist and it will always return an array.
   *
   * @param {number|array} rowIds A single ID as a number or an array of numbers
   * @return {array} Returns a cloned array of the row data for the given IDs
   *
   * @example
   * // Single ID
   * var id1 = bgt.getRowData(1);
   * => [{id: 1, data:{...}}]
   *
   * // Multiple ID
   * var ids = bgt.getRowData([1, 50]);
   * => [{id: 1, data:{...}}, {id: 50, data:{...}}]
   *
   * // No matching IDs
   * var noIds = bgt.getRowData(-1);
   * => []
   */
  BatchGeoTable.prototype.getRowData = function (rowIds) {
    rowIds = !(rowIds instanceof Array) ? [rowIds] : rowIds;
    const data = this.data.rows.filter((r) => rowIds.includes(r.id));
    // Must stay extend! Object.assign does NOT deep clone
    return $.extend(true, [], data);
  };

  /**
   * Loads all the plugins given to it in the plugins option. It calls the
   * plugin option with the initialized BatchGeoTable instance so plugins can
   * get the data, container, and so forth.
   */
  BatchGeoTable.prototype._loadPlugins = function () {
    var self = this;
    if (!this.options.plugins) return;

    this.options.plugins.forEach(function (plugin) {
      plugin.call(self);
    });
  };

  /**
   * Given an ID this method will scroll to the row.
   *
   * IMPORTANT: this assumes that the height of every row is the same. Without
   * this there is no way to figure out where a row is because as we add and
   * remove rows from the DOM to keep scrolling smooth the total height of the
   * scroll area and such would be changing. To force your rows to be the same
   * height without cutting content off add a `white-space: nowrap;` to your
   * CSS for your `td`s.
   */
  BatchGeoTable.prototype.scrollToRow = function (id, options, callback) {
    var els = this.getElements();
    var itemHeight = this._clusterize.options.item_height;

    // Allow setting the second argument to the callback
    if (typeof options === "function") {
      callback = options;
      options = {};
    }

    // We have to use clientHeight because jQuery's innerHeight does not work to
    // exclude the horizontal sidebar
    var scrollerHeight = $(els.scrollArea)[0].clientHeight;

    var settings = $.extend(
      {
        position: "top",
        animated: false,
      },
      options,
    );

    // Find the index in the data that's *displayed*, not the actual data index
    var displayIndex = this.getDisplayData()
      .map(function (row) {
        return row.id;
      })
      .indexOf(id);

    // By default itemHeight * the index in the DOM will give you the top of the
    // row's position which works out of the box for scrollTop.
    var scrollPosition = itemHeight * displayIndex;

    // Since we use `scrollTop` to move the scroller we need to calculate the
    // offset from the top when we aren't scrolling directly to the top of the
    // row. If we scroll to the middle we need to find the middle by getting the
    // scrollerHeight and divide by two *but* that will put the top of the row
    // in the middle, not the middle of the row in the middle. We then have to
    // get the middle of the row position. Once we have that we add that to the
    // top positioning to get the middle scroll offset.
    if (settings.position === "middle") {
      scrollPosition = scrollPosition + (itemHeight / 2 - scrollerHeight / 2);
    }
    // Bottom is identical to middle except we don't don't divide by 2 on the
    // scrollerHeight or row because we don't need the middle of the scroller.
    else if (settings.position === "bottom") {
      scrollPosition = scrollPosition + (itemHeight - scrollerHeight);
    }

    var self = this;
    clearTimeout(this._scrollerTimeout);
    // FIXME: No likey timeouts, need to figure out of the container is closing earlier
    // For now we need to make  sure we don't keep scrolling once the container is hidden
    // as the the height will always be 0
    this._scrollerTimeout = setTimeout(function () {
      // Don't bother scrolling if the container is hidden, the height will be 0
      if (!$(self.container).is(":visible")) {
        return;
      }
      // If animated is true animate the scroll top
      if (settings.animated) {
        // Before we animate jump close to where we are going so that on big data
        // sets it's not animating 10,000px or something. We check if we need to
        // scroll down or up and then after jumping to that position we animate
        // from that position (500px away) do the actual row position
        var scrollOffset =
          scrollPosition > $(els.scrollArea).scrollTop() ? -500 : 500;

        $(els.scrollArea).scrollTop(scrollPosition + scrollOffset);
        $(els.scrollArea).animate({ scrollTop: scrollPosition }, callback);
      }
      // Otherwise jump directly to the result instantly
      else {
        $(els.scrollArea).scrollTop(scrollPosition);
        if (typeof callback === "function") {
          callback();
        }
      }
    }, 50);

    return self;
  };

  /**
   * Returns if a row is visible based on the ID and scroll position. If it is
   * visible because the row is scrolled into the view it'll return true. If not
   * it will return false.
   *
   * @param  {number}  id The ID number for the row
   * @return {Boolean}    True if visible, false if not
   */
  BatchGeoTable.prototype.isRowVisible = function (id) {
    var els = this.getElements();
    var container = $(els.scrollArea);
    var $rowEl = $(els.contentContainer).find('[data-id="' + id + '"]');

    // If there's no $rowEl in the DOM its obviously not visible so return false
    if ($rowEl.length == 0) return false;

    var rowTop = $rowEl.offset().top - container.offset().top;
    var rowBottom = rowTop + $rowEl.height();

    return rowTop >= 0 && rowBottom <= container.height();
  };

  /**
   * Destroys the instance of BatchGeoTable by removing the element
   * BatchGeoTable element and tries to destroy plugins that were initialized.
   *
   * @return {undefined} You destroyed BatchGeoTable! There's nothing to return.
   */
  BatchGeoTable.prototype.destroy = function () {
    this.emit("destroy");
    $(this.getElements().container).find(".batchgeo-table").remove();
    this._handlers = {};
  };

  /**
   * Just like a normal JS event. Use like:
   * batchGeoTable.addListener('rowupdate', function () {
   *   // The rows were updated!
   * })
   */
  BatchGeoTable.prototype.addListener = function (event, callback) {
    var handlers = this._handlers;
    if (!handlers[event]) handlers[event] = {};
    handlers[event][callback] = callback;
    return callback;
  };

  /**
   * Just like a normal JS event. Use this to remove your listener like
   * batchGeoTable.removeListener('select', myCallback)
   */
  BatchGeoTable.prototype.removeListener = function (event, callback) {
    delete this._handlers[event][callback];
  };

  /**
   * Similar to jQuery trigger function, use this to emit an event. The
   * first param is the event name and after that you can add as many
   * following parameters as needed to be passed through to the listener
   */
  BatchGeoTable.prototype.emit = function () {
    var eventName = [].shift.apply(arguments);
    var eventArgs = arguments;
    var events = this._handlers[eventName];
    if (!events) return;
    Object.keys(events).forEach(function (handler) {
      events[handler].apply(self, eventArgs);
    });
  };

  window.BatchGeoTable = BatchGeoTable;
})(window);
